import { useEffect, useState } from 'react';
import { FileUp } from 'lucide-react';
import { toast } from 'sonner';
import { api, send, type Session } from '../api';
import type { InstructionsView } from '../../../src/application/carol/brief';
import { Button } from '../components/ui/button';
import { useAgentName } from '../agent-name';
import './simple-review.css';

/**
 * Instructions in the simple view (UI_MODE=simple): the client's collection policy in their own words. An owner uploads the
 * policy PDF (its text fills the box, to read and edit), or writes it, then saves. The first save also sets the
 * approve-everything permissions, so nothing changes about who decides: a person approves every email.
 * Everyone else reads it.
 */
const words = (text: string) => text.trim() ? text.trim().split(/\s+/).length : 0;
function readFile(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(new Error('This file could not be read.'));
    reader.readAsDataURL(file);
  });
}

export function SimpleInstructions({ session }: { session: Session }) {
  const agent = useAgentName();
  const owner = session.actor.roles.includes('owner');
  const [view, setView] = useState<InstructionsView | null>(null);
  const [draft, setDraft] = useState(''); const [busy, setBusy] = useState(''); const [error, setError] = useState('');
  const apply = (next: InstructionsView) => { setView(next); setDraft(next.instructions?.body ?? next.template); };
  useEffect(() => { void api<InstructionsView>('/app-api/carol/instructions').then(apply).catch(e => setError(e instanceof Error ? e.message : 'This page could not load.')); }, []);

  async function upload(file: File | undefined) {
    if (!file || busy) return;
    setBusy('reading'); setError('');
    try {
      const { text } = await api<{ text: string }>('/app-api/carol/instructions/extract', { contentBase64: await readFile(file) });
      setDraft(text.trim());
      toast.success(`Read ${file.name}. Check the text, then save.`);
    } catch (e) { setError(e instanceof Error ? e.message : 'This PDF could not be read.'); }
    finally { setBusy(''); }
  }
  async function save() {
    if (!view || busy) return;
    setBusy('saving'); setError('');
    try {
      let next = await send<InstructionsView>('/app-api/carol/instructions', 'PUT', { body: draft });
      // First save: every email waits for a person, as it does today.
      if (!next.permissions) next = await send<InstructionsView>('/app-api/carol/permissions', 'PUT', next.presets.aiprise.permissions);
      apply(next); toast.success('Saved.');
    } catch (e) { setError(e instanceof Error ? e.message : 'This could not be saved.'); }
    finally { setBusy(''); }
  }
  const count = words(draft); const tooLong = view ? count > view.maxWords : false;
  const saved = view?.instructions;

  return <main id="main-content" className="sr-page si-page">
    <header className="sr-head"><h1>Instructions</h1>
      <p>How you collect, in your own words. {agent} follows this in every email, and you approve each one before it goes out.</p></header>
    {error && <div className="alert error" role="alert">{error}</div>}
    {view && <section className="si-card" aria-label="Instructions">
      {owner && <div className="si-row">
        <label className="si-upload"><input type="file" accept="application/pdf" onChange={e => void upload(e.target.files?.[0])}/>
          <Button asChild variant="outline"><span><FileUp aria-hidden/>{busy === 'reading' ? 'Reading…' : 'Upload your policy (PDF)'}</span></Button></label>
        <span className="muted">{saved ? `Last saved ${new Date(saved.createdAt).toLocaleDateString()} by ${saved.author}` : 'Not saved yet'}</span>
      </div>}
      <textarea aria-label="Instructions" value={draft} readOnly={!owner} onChange={e => setDraft(e.target.value)}/>
      {owner && <div className="si-row">
        <span className="muted">{tooLong ? `Too long: keep it under about ${view.maxWords.toLocaleString()} words.` : `${count.toLocaleString()} words`}</span>
        <Button disabled={Boolean(busy) || tooLong || !draft.trim() || draft === saved?.body} onClick={() => void save()}>
          {busy === 'saving' ? 'Saving…' : 'Save'}</Button></div>}
    </section>}
    <section className="si-card" aria-label="Always true">
      <h2>Always true, whatever the instructions say</h2>
      <p>{agent} uses only the amounts, dates and invoice numbers in your records, writes only to contacts on file, and never
        changes bank details or moves money.</p>
    </section>
  </main>;
}
